/**
 * The two sections every governance dialog is made of.
 *
 * Access is granted at three granularities -- a whole database or dashboard, one
 * of its tables, one of its columns -- and each is configured from its own
 * dialog. All three ask the same two questions, so all three draw the same two
 * sections, and the wording of the second one is written once here rather than
 * three times.
 *
 * `Member access` is the part that differs per granularity, so each dialog
 * supplies its own body. `MCP access` is a single switch everywhere: a column,
 * table or application is open to AI by default, and the only thing to say
 * about it is whether to close it.
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'

export function AccessSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-sm font-medium">{title}</h3>
        {description !== undefined && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </section>
  )
}

/**
 * The whole `MCP access` section: its heading and its one switch.
 *
 * Phrased as access rather than as restriction, and checked when open, so it
 * reads the same way round as the member switches above it -- on means allowed.
 *
 * @param id Distinct per dialog, because two of them can be open over the same
 *   page and a repeated id would point the label at the wrong switch.
 */
export function McpAccessSection({
  id,
  description,
  checked,
  onCheckedChange,
}: {
  id: string
  description: string
  checked: boolean
  onCheckedChange: (next: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <AccessSection title={t('mcpAccess.title')} description={description}>
      <div className="flex items-center justify-between rounded border p-2">
        <Label htmlFor={id} className="text-sm font-normal">
          {t('mcpAccess.allow')}
        </Label>
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      </div>
    </AccessSection>
  )
}
